import { Context, InitOptions } from '@growthbook/growthbook';
import { GrowthbookClientProvider } from '@openfeature/growthbook-client-provider';
import { Client, OpenFeature, Provider } from '@openfeature/web-sdk';
import {
  Environment,
  Environments,
  getCookieTokens,
  getEnvironment,
} from '@schemeserve/global-utils';
import { JWTUser } from 'accounting/types';
import { pipe } from 'fp-ts/lib/function';
import * as O from 'fp-ts/lib/Option';
import jwtDecode from 'jwt-decode';

export type FeatureFlagContext = {
  siteId: string;
  agentId: string;
  userId: string;
  clientId: string;
  origin: string;
  url: string;
  path: string;
};

export default async function setUpFeautreFlags(
  provider: Provider,
  getContext: () => O.Option<FeatureFlagContext>,
): Promise<Client> {
  const context = pipe(
    getContext(),
    O.getOrElse(() => ({
      origin: document.location.host,
      url: window.location.href,
      path: window.location.pathname,
    })),
  );

  await OpenFeature.setProviderAndWait(provider, context);

  return OpenFeature.getClient();
}

function getClientKeyByEnv(env: Environment): string {
  const keyByEnv = {
    [Environments.dev]: process.env.FEATURE_FLAGS_KEY_DEV,
    [Environments.labs]: process.env.FEATURE_FLAGS_KEY_LABS,
    [Environments.beta]: process.env.FEATURE_FLAGS_KEY_BETA,
    [Environments.live]: process.env.FEATURE_FLAGS_KEY_LIVE,
  };

  return keyByEnv?.[env] ?? '';
}

export function getGrowthbookProvider(): GrowthbookClientProvider {
  const env = getEnvironment(window.location.origin);
  const clientKey = getClientKeyByEnv(env);
  /*
   * Configure your GrowthBook instance with GrowthBook context
   * @see https://docs.growthbook.io/lib/js#step-1-configure-your-app
   */
  const gbContext: Context = {
    apiHost: process.env.FEATURE_FLAGS_DOMAIN,
    clientKey,
    enableDevMode: true,
  };

  /*
   * optional init options
   * @see https://docs.growthbook.io/lib/js#switching-to-init
   */
  const initOptions: InitOptions = {
    timeout: 2000,
    streaming: false,
  };

  return new GrowthbookClientProvider(gbContext, initOptions);
}

// TODO: what should we do if user isn't logged in
export function buildUserContext(): O.Option<FeatureFlagContext> {
  return pipe(
    getCookieTokens(),
    O.map((d) => jwtDecode(d.accessToken) as JWTUser),
    O.map((d) => ({
      siteId: d.MasterAccountId,
      agentId: d.AccountId,
      userId: d.UserId,
      clientId: d.ClientId,
      userGroup: d.AccessGroup,
      origin: document.location.host,
      url: window.location.href,
      path: window.location.pathname,
    })),
  );
}
